<template>
  <div class="native-notices" aria-live="polite" aria-atomic="false">
    <div
      v-for="notice in notices"
      :key="notice.id"
      class="native-notice"
      :class="`native-notice-${notice.type}`"
      role="status"
    >
      {{ notice.message }}
      <button type="button" aria-label="关闭通知" @click="removeNotice(notice.id)">×</button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from "vue";

type NoticeType = "success" | "error" | "info";
type Notice = { id: number; type: NoticeType; message: string };

const notices = ref<Notice[]>([]);
const timers = new Map<number, ReturnType<typeof setTimeout>>();
let nextId = 0;

const removeNotice = (id: number) => {
  notices.value = notices.value.filter((notice) => notice.id !== id);
  const timer = timers.get(id);
  if (timer) clearTimeout(timer);
  timers.delete(id);
};

const handleNotice = (event: Event) => {
  const detail = (event as CustomEvent<{ type: NoticeType; message: string }>).detail;
  const id = ++nextId;
  notices.value.push({ id, ...detail });
  timers.set(id, setTimeout(() => removeNotice(id), 3500));
};

onMounted(() => window.addEventListener("native-notice", handleNotice));
onBeforeUnmount(() => {
  window.removeEventListener("native-notice", handleNotice);
  timers.forEach(clearTimeout);
});
</script>

<style scoped>
.native-notices {
  position: fixed;
  z-index: 2000;
  top: 18px;
  right: 18px;
  display: grid;
  gap: 8px;
  width: min(360px, calc(100vw - 36px));
}

.native-notice {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 14px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #fff;
  color: #334155;
  box-shadow: 0 8px 24px rgb(15 23 42 / 12%);
}

.native-notice-success { border-color: #86c6a0; }
.native-notice-error { border-color: #e6a0a0; color: #9b2525; }
.native-notice-info { border-color: #91b8d8; }

button {
  border: 0;
  background: none;
  color: inherit;
  font-size: 1.2rem;
  cursor: pointer;
}
</style>
